import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Facebook, Instagram } from "lucide-react";
import HotdogIcon from "./HotdogIcon";
import { SOCIAL } from "@/lib/data";

const LINKS = [
    { id: "menuer", label: "Menuer" },
    { id: "vognen", label: "Vognen" },
    { id: "faq", label: "FAQ" },
];

export default function NavBar({ onNavigate }) {
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const linkTone = scrolled
        ? "text-muted-foreground hover:text-ink"
        : "text-paper/75 hover:text-paper";
    const socialTone = scrolled
        ? "text-muted-foreground hover:bg-sky-soft hover:text-poelse"
        : "text-paper/80 hover:bg-paper/10 hover:text-gold";

    return (
        <motion.header
            initial={{ y: -90 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,border-color] duration-300 ${
                scrolled
                    ? "border-b border-border bg-paper/85 shadow-[0_8px_30px_rgba(16,37,49,0.08)] backdrop-blur-xl"
                    : "bg-transparent"
            }`}
        >
            <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4">
                <button
                    data-testid="nav-logo"
                    onClick={() => onNavigate("top")}
                    className="group flex items-center gap-2.5"
                >
                    <span className="grid h-9 w-11 place-items-center rounded-lg bg-gold text-poelse transition-transform duration-300 group-hover:-rotate-6">
                        <HotdogIcon className="h-4 w-7" />
                    </span>
                    <span
                        className={`font-serif text-lg font-bold tracking-tight transition-colors duration-300 ${
                            scrolled ? "text-ink" : "text-paper"
                        }`}
                    >
                        Lej En Pølsevogn.dk
                    </span>
                </button>
                <nav className="hidden items-center gap-8 md:flex">
                    {LINKS.map((l) => (
                        <button
                            key={l.id}
                            data-testid={`nav-link-${l.id}`}
                            onClick={() => onNavigate(l.id)}
                            className={`group relative font-mono text-xs uppercase tracking-[0.2em] transition-colors duration-200 ${linkTone}`}
                        >
                            {l.label}
                            <span className="absolute -bottom-1 left-0 h-px w-0 bg-gold transition-[width] duration-300 group-hover:w-full" />
                        </button>
                    ))}
                </nav>
                <div className="flex items-center gap-1">
                    <a
                        data-testid="nav-facebook-link"
                        href={SOCIAL.facebook}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Find os på Facebook"
                        title="Find os på Facebook"
                        className={`hidden h-9 w-9 place-items-center rounded-full transition-[color,background-color,transform] duration-200 active:scale-90 sm:grid ${socialTone}`}
                    >
                        <Facebook className="h-4 w-4" />
                    </a>
                    <a
                        data-testid="nav-instagram-link"
                        href={SOCIAL.instagram}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Find os på Instagram"
                        title="Find os på Instagram"
                        className={`hidden h-9 w-9 place-items-center rounded-full transition-[color,background-color,transform] duration-200 active:scale-90 sm:grid ${socialTone}`}
                    >
                        <Instagram className="h-4 w-4" />
                    </a>
                    <button
                        data-testid="nav-cta"
                        onClick={() => onNavigate("byg-din-event")}
                        className="ml-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-poelse transition-[background-color,transform] duration-200 hover:bg-gold-deep active:scale-95"
                    >
                        Byg din forespørgsel
                    </button>
                </div>
            </div>
        </motion.header>
    );
}
